// AC GUIDE 3: THE DIFFERENCE BETWEEN INVERTER AND NON-INVERTER ACs (Oct 2026).
// /blog/difference-between-inverter-and-non-inverter-ac
//
// Rebuilt 20 Oct 2026 to GUIDES_PLAYBOOK.md section 3. The "how it works"
// guide: a non-inverter is either fully on or fully off; an inverter slows
// down instead, like a car cruising instead of braking and speeding up. Which
// one to buy is guide 1; this page links to it rather than repeating it.
//
// Saheb likes the three animations ("inverter non inverter ones are good -
// need those"), so they stay: components/guide/CompressorExplainer.jsx under
// the answer, FrequencyExplainer.jsx and InsideExplainer.jsx in their parts,
// each now playing by itself, in 8th-standard words, with no Hz, kW or volts
// on screen.
//
// Same rules as the other guides: every fact from Daikin, the Government of
// India or Citi Cool; each claim with a source key; the page and the copy
// Google reads both build from diffContent(). No imports. **bold**.

export const DIFF_SLUG = 'blog/difference-between-inverter-and-non-inverter-ac';
export const DIFF_PUBLISHED = '2026-10-17';
export const DIFF_UPDATED = '2026-10-20';

export const DIFF_SEO = {
  title: 'Difference Between Inverter and Non-Inverter AC, Explained Simply | Citi Cool',
  meta: 'A non-inverter AC is fully on or fully off. An inverter slows down instead, like a car cruising. Watch both cool a room and see what is different inside.',
  eyebrow: 'AC Guide · How it works',
  h1: 'What is the difference between an inverter and a non-inverter AC?',
  crumb: 'Inverter and non-inverter, explained',
  readMins: 4,
};

const KW_PER_TR = 3.517;
const REPAIRS = { pcb: 3500, capacitor: 1500 };   // our service team, Oct 2026 (Saheb)
const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

// ─── sources ──────────────────────────────────────────────────────────────

export const DIFF_SOURCES = [
  { key: 'daikin-tech', label: 'Inverter Technology: how inverter and non-inverter ACs control the compressor', by: 'Daikin India', href: 'https://daikinindia.com/inverter-technology-0' },
  { key: 'daikin-blog-tech', label: 'Daikin Inverter Technology: "An inverter is a device for converting frequency"', by: 'Daikin India', href: 'https://daikinindia.com/blog-details/daikin-inverter-technology-making-daikin-air-conditioners-best-air-conditioners-india' },
  { key: 'daikin-how', label: 'How Inverter AC Technology Works', by: 'Daikin India', href: 'https://daikinindia.com/blog-details/how-inverter-ac-technology-works' },
  { key: 'daikin-demyst', label: 'Inverter Technology Demystified: How Variable Speed Compressors Save You Money', by: 'Daikin India', href: 'https://daikinindia.com/blog-details/inverter-technology-demystified-how-variable-speed-compressors-save-you-money' },
  { key: 'daikin-choosing', label: 'Choosing the Right Cooling Solution: Comparing Inverter and Non-Inverter 1.5 Ton Split ACs', by: 'Daikin India', href: 'https://daikinindia.com/blog-details/choosing-right-cooling-solution-comparing-inverter-and-non-inverter-1.5-ton-split-acs' },
  { key: 'daikin-faq', label: 'FAQs: what an inverter AC is', by: 'Daikin India', href: 'https://daikinindia.com/faqs' },
  { key: 'daikin-specs', label: "Each model's page: outdoor unit size and lowest cooling (GTQ50 and GTKL50 outdoor units both 595 × 845 × 300 mm)", by: 'Daikin India', href: 'https://daikinindia.com/product-category/splitac' },
  { key: 'daikin-warranty', label: 'Product Warranty', by: 'Daikin India', href: 'https://www.daikinindia.com/node/34556' },
  { key: 'daikin-label', label: 'BEE star label figures from the Daikin RA Master Catalogue 2026, shown on each product page', by: 'Daikin', href: '/daikin-1-5-ton-ac-price' },
  { key: 'calc', label: "Our room model: Daikin's method for how much heat gets into a room, hour by hour, on IMD weather for Delhi, as in our AC calculator", by: 'Citi Cool engineering', href: '/ac-calculator' },
  { key: 'service', label: 'Repair costs and breakdowns seen by our own service team, October 2026', by: 'Citi Cool', href: null },
  { key: 'saheb', label: 'Our showroom\'s buying advice: inverter for long-use bedrooms, non-inverter for short use, top floors, open areas and low upkeep (October 2026)', by: 'Citi Cool', href: null },
];

// ─── the moments for "how does an inverter control its speed?" ────────────

// One 12 × 12 ft bedroom in Delhi, west window, set to 24°C. The page runs
// each moment through the room model (momentCheck in data/roomDay.js).
const BEDROOM = { city: 'delhi', kind: 'bedroom', size: '12x12', floor: 'mid', sun: 'W', setC: 24, day: 'typical' };
export const MOMENTS = [
  { key: 'evening', label: 'A warm evening', room: BEDROOM, h: 22 },
  { key: 'afternoon', label: 'A hot afternoon', room: BEDROOM, h: 16 },
  { key: 'top', label: 'Top floor, hottest day', room: { ...BEDROOM, floor: 'top', day: 'hottest' }, h: 16 },
];
export const NIGHT = { key: 'night', room: BEDROOM, h: 3 };

// How much of the time each compressor runs, 0..1, and its speed when it
// does, for the animation. `row` from momentCheck(); spec from AC_SPECS.
export function laneMotion(row, ac, spec = {}) {
  if (row.state === 'flat') return { on: 1, speed: 1, words: 'Full speed, all the time' };
  if (!ac.model.inverter) return { on: Math.max(0.1, row.share), speed: 1, words: `On ${Math.round(row.share * 10)} minutes in every 10` };
  const minShare = spec.minKw ? spec.minKw / (row.capKw || ac.model.kw) : 0.3;
  if (row.state === 'min') return { on: Math.max(0.1, row.share / minShare), speed: minShare, words: 'Slowest speed, sometimes off' };
  const sp = Math.max(minShare, row.share);
  return { on: 1, speed: sp, words: sp < 0.4 ? 'Always on, running slowly' : sp < 0.75 ? 'Always on, at about half speed' : 'Always on, nearly full speed' };
}

// ─── the page ─────────────────────────────────────────────────────────────

// g: pickGuideModels() for 1.5 ton; specs: AC_SPECS; night: momentCheck()
// for NIGHT with [fixed, three] (or null).
export function diffContent(g, specs = {}, night = null) {
  const F = g.fixed; const T = g.three;
  const sF = specs[F.model.id] || {}; const sT = specs[T.model.id] || {};
  const sameBox = sF.odu && sT.odu && sF.odu === sT.odu;
  const nightLow = night && sT.minKw && night.needTR < sT.minKw / KW_PER_TR;

  return {
    answer: {
      text: '**A non-inverter AC is either fully on or fully off. An inverter AC slows down instead of switching off.** Think of a car cruising, instead of braking and speeding up again.',
      src: ['daikin-tech'],
    },
    because: [
      { text: 'So an inverter keeps the room steadier and runs more quietly.', src: ['daikin-demyst'] },
      { text: 'It is cheaper to run in a room it can cool fully.', src: ['daikin-tech', 'calc'] },
      { text: 'In a room [too big](/blog/what-size-ac-do-i-need) or too hot for it, both run at full power. Then they work the same.', src: ['calc'] },
    ],
    watch: {
      title: 'Watch: inverter and non-inverter AC cooling a bedroom',
      captions: [
        { at: 0, text: 'Both ACs start at full power to cool a hot bedroom.' },
        { at: 0.24, text: 'At 24°C the non-inverter switches off. When the room warms, it starts again.' },
        { at: 0.45, text: 'The inverter slows down instead. The room stays at a steady 24°C.' },
        { at: 0.74, text: 'Now the afternoon sun is too strong. Both run at full power. They work the same.' },
      ],
      note: 'A simple picture of how each kind works, drawn from Daikin\'s own explanation.',
      noteSrc: ['daikin-tech'],
    },
    table: {
      title: 'Inverter vs non-inverter AC: the differences',
      head: ['', 'Non-inverter', 'Inverter'],
      rows: [
        ['How it runs', 'Fully on or off', 'Slows down, speeds up'],
        ['Comfort', 'Room warms and cools a little', 'Steady'],
        ['Noise', 'Loud at each start', 'Quieter'],
        ['Electricity', 'More, in a room it can cool', 'Less, in a room it can cool'],
        ['Repair cost', `Low (about ${inr(REPAIRS.capacitor)})`, `High (about ${inr(REPAIRS.pcb)})`],
      ],
      note: 'In a room the AC can\'t cool fully, both use the same power.',
      src: ['daikin-tech', 'daikin-demyst', 'service', 'calc'],
    },
    sides: [
      {
        key: 'inverter',
        title: 'Inverter AC: pros and cons',
        good: [
          { text: 'The room stays at a steady temperature.', src: ['daikin-faq'] },
          { text: 'It is quieter. It does not keep starting at full blast.', src: ['daikin-demyst'] },
          { text: 'Its compressor wears less, says Daikin.', src: ['daikin-how'] },
          { text: 'It uses less power when it can slow down.', src: ['daikin-tech'] },
        ],
        bad: [
          { text: `Its circuit board is costly. A repair after warranty is about ${inr(REPAIRS.pcb)}.`, src: ['service'] },
          { text: 'At full power, it saves nothing.', src: ['calc'] },
          { text: 'It costs more to buy.', src: ['daikin-choosing'] },
        ],
      },
      {
        key: 'fixed',
        title: 'Non-inverter AC: pros and cons',
        good: [
          { text: 'Its parts are simple and easy to repair, says Daikin.', src: ['daikin-choosing'] },
          { text: `It is very sturdy. A capacitor repair costs about ${inr(REPAIRS.capacitor)}.`, src: ['service'] },
          { text: 'At full power, it works just like an inverter.', src: ['calc'] },
        ],
        bad: [
          { text: 'The room warms and cools a little as it switches.', src: ['daikin-demyst'] },
          { text: 'It starts at full blast each time, so it is louder.', src: ['daikin-demyst'] },
          { text: 'It uses more [units](/blog/ac-power-consumption-units-per-hour) in a room it can cool fully.', src: ['daikin-label'] },
        ],
      },
    ],
    parts: [
      {
        id: 'speed',
        q: 'How does an inverter AC control its speed?',
        bullets: [
          { text: 'The compressor is the heart of an AC. It pumps the cooling gas around.', src: ['daikin-tech'] },
          { text: 'A non-inverter has a simple switch. The compressor is fully on or off.', src: ['daikin-tech'] },
          { text: 'An inverter has a circuit board. It changes the power, so the compressor runs slower or faster.', src: ['daikin-blog-tech'] },
        ],
      },
      {
        id: 'inside',
        q: 'What is different inside an inverter AC?',
        bullets: [
          { text: sameBox ? `From outside, the two look the same. The ${F.code} and ${T.code} outdoor units are the same size.` : 'From outside, the two look much the same.', src: sameBox ? ['daikin-specs'] : [] },
          { text: 'Inside, the inverter has a speed-changing compressor and a circuit board.', src: ['daikin-tech'] },
          { text: `Daikin covers that board for 5 years. A repair after that costs about ${inr(REPAIRS.pcb)}.`, src: ['daikin-warranty', 'service'] },
        ],
      },
      {
        id: 'quiet',
        q: 'Which is quieter, an inverter or a non-inverter AC?',
        bullets: [
          { text: 'The inverter. Daikin says its inverter ACs run more quietly.', src: ['daikin-demyst'] },
          { text: 'A non-inverter starts at full blast each time the room warms up.', src: ['daikin-demyst'] },
          { text: 'An inverter just hums along at a lower speed.', src: ['daikin-tech'] },
        ],
      },
      {
        id: 'life',
        q: 'Which lasts longer, an inverter or a non-inverter AC?',
        bullets: [
          { text: 'Daikin says an inverter\'s compressor wears less. It does not keep stopping and starting.', src: ['daikin-how'] },
          { text: 'Our service team finds non-inverters very sturdy. They rarely break down.', src: ['service'] },
          { text: 'Daikin covers an inverter\'s compressor for 10 years, and a non-inverter\'s for 5.', src: ['daikin-warranty'] },
        ],
      },
    ],
    faq: [
      {
        q: 'What is the difference between inverter and non-inverter AC?',
        a: 'A non-inverter runs at one speed. It switches fully on and off. An inverter changes its speed to match the room.',
        src: ['daikin-tech'],
      },
      {
        q: 'What does inverter mean in an AC?',
        a: 'It is a circuit board that changes how fast the compressor runs. It has nothing to do with a home inverter or battery.',
        src: ['daikin-blog-tech'],
      },
      {
        q: 'Does an inverter AC switch off when the room is cool?',
        a: nightLow
          ? 'Mostly it slows down instead. On a cool night it may still switch off and on, at its slowest speed.'
          : 'Mostly it slows down instead. It switches off only when the room needs very little cooling.',
        src: nightLow ? ['daikin-specs', 'calc'] : ['daikin-tech'],
      },
      {
        q: 'Do inverter and non-inverter ACs look different?',
        a: 'Hardly. The difference is inside the outdoor unit: the compressor and the circuit board.',
        src: sameBox ? ['daikin-specs', 'daikin-tech'] : ['daikin-tech'],
      },
      {
        q: 'Does an inverter AC cool faster?',
        a: 'It starts at full power, like a non-inverter. Once the room is cool, it slows down instead of stopping.',
        src: ['daikin-blog-tech'],
      },
      {
        q: 'Which one should I buy?',
        a: 'An inverter for a bedroom used 6 or more hours a day. A non-inverter for short use, a top floor or a hall. Our [inverter vs non-inverter guide](/blog/inverter-vs-non-inverter-ac) explains why.',
        src: ['saheb'],
      },
    ],
  };
}

// Words for the three parts of each outdoor unit in InsideExplainer.
export const INSIDE = {
  fixed: [
    { part: 'compressor', name: 'Compressor: one speed' },
    { part: 'board', name: 'Simple switch and capacitor' },
  ],
  inverter: [
    { part: 'compressor', name: 'Compressor: changes speed' },
    { part: 'board', name: 'Circuit board that sets the speed' },
  ],
};
export const INSIDE_CAPTIONS = [
  { at: 0, text: 'From outside, both outdoor units look much the same.' },
  { at: 0.25, text: 'A non-inverter has a one-speed compressor and a simple switch.' },
  { at: 0.55, text: 'An inverter has a compressor that changes speed, and a circuit board to control it.' },
  { at: 0.8, text: `That board is costly. A repair after warranty is about ${inr(REPAIRS.pcb)}.` },
];

export const SPEED_CAPTIONS = [
  { at: 0, text: 'Both ACs get the same power from the wall socket.' },
  { at: 0.2, text: 'The non-inverter\'s switch sends it all, or nothing. Its compressor is on or off.' },
  { at: 0.45, text: 'The inverter\'s circuit board changes the power. Its compressor runs slower or faster.' },
  { at: 0.75, text: 'On the hottest top-floor afternoon, both run at full speed.' },
];
